import "./OrganizationManagement.css";

import React, { useEffect, useMemo, useState } from "react";
import {
  addNewOrganization,
  getBusinessUnitsList,
  getCompanyTiers,
  getLegalEntitiesList,
  getParentOrganization,
  getSectorsList,
  getSubBusinessUnitsList,
  updateOrganization,
  uploadOrganizationFiles,
  downloadOrgFile
} from "../../../services/svt/organization-management.service";

import IconButton from "../../common/IconButton/IconButton";
import Loader from "../../common/Loader/Loader";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import PlusIcon from "../../../assets/PlusIcon";
import { getAllRegionsWithCountry } from "../../../services/svt/svt-admin/rcm-management.service";
import { sortByCreatedDateDesc } from "../../../utils/sortByDate";
import { toast } from "react-toastify";
import validateFields from "../../../utils/ValidationInput";
import DownloadIcon from "../../../assets/Download.svg";
import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../utils/dateFormat";
import { useTranslation } from "react-i18next";

// const columns = [
//     { name: "Name", field: "organizationName", selector: row => row.organizationName, sortable: true, width: "250px" },
//     { name: "Type", field: "type", selector: row => row.type, sortable: true },
//     { name: "Immediate Parent", field: "immediateParent", selector: row => row.immediateParent, sortable: true },
//     { name: "Region", field: "region", selector: row => row.region, sortable: true },
//     { name: "Country", field: "country", selector: row => row.country, sortable: true },
//     { name: "Added Date", field: "addedDate", selector: row => row.addedDate, cell: row => formatDate(row.addedDate), sortable: true },
//     { name: "Added By", field: "addedBy", selector: row => row.addedBy, sortable: true },
// ];

const OrganizationManagement = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);

  const columns = useMemo(() => [
    { name: t("name"), field: "organizationName", selector: row => row.organizationName, sortable: true, width: "250px" },
    { name: t("type"), field: "type", selector: row => row.type, sortable: true },
    { name: t("immediateParent"), field: "immediateParent", selector: row => row.immediateParent, sortable: true },
    { name: t("region"), field: "region", selector: row => row.region, sortable: true },
    { name: t("country"), field: "country", selector: row => row.country, sortable: true },
    { name: t("addedDate"), field: "addedDate", selector: row => row.addedDate, cell: row => formatDate(row.addedDate), sortable: true },
    { name: t("addedBy"), field: "addedBy", selector: row => row.addedBy, sortable: true },
  ], [t, i18n.language]);

  const [filterType, setFilterType] = useState("sector");
  const [organizations, setOrganizations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add"); // 'add' | 'edit' | 'upload'
  const [organizationData, setOrganizationData] = useState({
    organizationName: "",
    type: "",
    immediateParent: "",
    regionName: "",
    countryName: "",
    tierCode: "",
    parentorgId: "",
    parentId: "",
  });
  const [formData, setFormData] = useState({
    orgId: "",
    file: "",
  });

  const [editingEntity, setEditingEntity] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [companyTiers, setCompanyTiers] = useState([]);
  const [parentList, setParentList] = useState([]);
  const [subBUList, setSubBUList] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState("");
  const [perPageCount, setPerPageCount] = useState("");
  const [filteredColumns, setFilteredColumns] = useState(columns);
  const [regionList, setRegionList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("");

  const immediateParentMapping = {
    [t("sector")]: [""],
    [t("businessUnit")]: [t("sector")],
    [t("subBusinessUnit")]: [t("businessUnit"), t("sector")],
    [t("legalEntity")]: [t("businessUnit"), t("subBusinessUnit"), t("sector")],
  };
  const isEditMode = modalType === "edit";
  const isAddMode = modalType === "add";
  const isUploadMode = modalType === "upload";

  useEffect(() => {
    if (filterType === "sector") {
      fetchSectors();
      const filteredColumnsList = columns.filter(
        (col) => col.field !== "region" && col.field !== "country"
      );
      setFilteredColumns(filteredColumnsList);
    }
    fetchAllRegionsWithCountry(); // Used for fetching all regions
    // Add columns and filterType as dependencies
  }, [columns, filterType]);

  // Fetch all regions with country
  const fetchAllRegionsWithCountry = async () => {
    try {
      setLoading(true);
      const allRegionsList = await getAllRegionsWithCountry();
      if (allRegionsList !== null) {
        const updatedData = allRegionsList?.data?.map((regionItem) => ({
          ...regionItem,
          value: regionItem.regionId,
          label: regionItem.regionName,
        }));
        setRegionList(updatedData);

        // Collect countries for first region
        const selectedRegionCountries = updatedData.filter(
          (item) => item.regionId === updatedData[0].regionId
        );
        let allCountries = selectedRegionCountries[0].countries?.map(
          (item) => ({
            ...item,
            value: item.countryId,
            label: item.countryName,
          })
        );
        setCountryList(allCountries);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTierName = (code) => {
    let tierName = "";
    if (code === "SEC") tierName = t("sector");
    if (code === "BU") tierName = t("businessUnit");
    if (code === "SBU") tierName = t("subBusinessUnit");
    if (code === "LE") tierName = t("entity");
    return tierName;
  };

  // Fetch all sectors
  const fetchSectors = async (type) => {
    try {
      setLoading(true);
      const sectorsList = await getSectorsList();
      const updatedData = sectorsList?.data.map((item) => ({
        ...item,
        organizationName: item.name,
        type: getTierName(item.tierCode),
        immediateParent: item.parentOrgName,
        region: "-",
        country: "-",
        addedDate: item.createdDate,
        addedBy: item.createdBy,
      }));
      if (!type)
        setOrganizations(
          sortByCreatedDateDesc(updatedData, "createdDate")
        );

      // Set Immediate Parent list in modal
      if (type) {
        const immediateParentList = sectorsList?.data.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        if (type !== "editmodal") {
          setOrganizationData((prevData) => ({
            ...prevData,
            parentorgId: immediateParentList[0].id,
            parentId: immediateParentList[0].parentOrgId,
          }));
        }
        setParentList(immediateParentList);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all business units
  const fetchBusinessUnits = async (type, selectedRow) => {
    try {
      setLoading(true);
      const buList = await getBusinessUnitsList();
      const updatedData = buList?.data.map((item) => ({
        ...item,
        organizationName: item.name,
        type: getTierName(item.tierCode),
        immediateParent: item.parentOrgName,
        region: "-",
        country: "-",
        addedDate: item.createdDate,
        addedBy: item.createdBy,
      }));
      if (!type)
        setOrganizations(
          sortByCreatedDateDesc(updatedData, "createdDate")
        );

      // Set Immediate Parent list in modal
      if (type) {
        const immediateParentList = buList?.data.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        if (type !== "editmodal") {
          setOrganizationData((prevData) => ({
            ...prevData,
            parentorgId: immediateParentList[0].id,
            parentId: immediateParentList[0].parentOrgId,
          }));
        }
        setParentList(immediateParentList);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all sub business units
  const fetchSubBusinessUnits = async (type) => {
    try {
      setLoading(true);
      const subBUList = await getSubBusinessUnitsList();
      const updatedData = subBUList?.data.map((item) => ({
        ...item,
        organizationName: item.name,
        type: getTierName(item.tierCode),
        immediateParent: item.parentOrgName,
        region: "-",
        country: "-",
        addedDate: item.createdDate,
        addedBy: item.createdBy,
        label: item.name,
        value: item.id,
      }));
      if (!type)
        setOrganizations(
          sortByCreatedDateDesc(updatedData, "createdDate")
        );

      // Set Immediate Parent list in modal
      if (type) {
        const immediateParentList = subBUList?.data.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        if (type !== "editmodal") {
          setOrganizationData((prevData) => ({
            ...prevData,
            parentorgId: immediateParentList[0].id,
            parentId: immediateParentList[0].parentOrgId,
          }));
        }
        setParentList(immediateParentList);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParentOrganization = async () => {
    try {
      setLoading(true);
      const organizationList = await getParentOrganization();
      const updatedData = organizationList?.data.map((item) => ({
        ...item,
        label: item.name,
        value: item.parentOrgId,
      }));
      setOrganizationData((prevData) => ({
        ...prevData,
        parentorgId: organizationList?.data[0].id,
        parentId: organizationList?.data[0].id,
      }));
      setParentList(updatedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all legal entites
  const fetchLegalEntities = async () => {
    try {
      setLoading(true);
      const legalEntityList = await getLegalEntitiesList();
      const updatedData = legalEntityList?.data.map((item) => ({
        ...item,
        organizationName: item.name,
        type: getTierName(item.tierCode),
        immediateParent: item.parentOrgName,
        region: item.regionName,
        country: item.countryName,
        addedDate: item.createdDate,
        addedBy: item.createdBy,
      }));
      setPerPageCount(50);
      setOrganizations(sortByCreatedDateDesc(updatedData, "createdDate"));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const tierList = await getCompanyTiers();
      const filteredList = tierList?.data.filter(
        (item) => item.tiercode !== "PO"
      );
      const updatedData = filteredList.map((item) => ({
        ...item,
        label: item.tiername === 'BusinessUnit' ? "Business Unit" : item.tiername === 'SubBusinessUnit' ? "Sub-Business Unit" : item.tiername,
        value: item.id,
      }));
      setOrganizationData({
        organizationName: "",
        regionName: "",
        countryName: "",
        tierCode: filteredList[0].tiercode,
        type: filteredList[0].id,
      });
      setCompanyTiers(updatedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubBUList = async () => {
    try {
      setLoading(true);
      const subBUList = await getSubBusinessUnitsList();
      const updatedData = subBUList?.data.map((item) => ({
        ...item,
        organizationName: item.name,
        type: item.tierCode,
        label: item.name,
        value: item.id,
      }));
      setFormData((prevData) => ({
        ...prevData,
        orgId: subBUList?.data[0].id,
      }));
      setSubBUList(updatedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSearchData = () => {
    if (filterType === "sector") fetchSectors();
    else if (filterType === "businessUnit") fetchBusinessUnits();
    else if (filterType === "subBusinessUnit") fetchSubBusinessUnits();
    else if (filterType === "legalEntity") fetchLegalEntities();
    setSelectedFilter(filterType);

    if (filterType !== "legalEntity") {
      const filteredColumnsList = columns.filter(
        (col) => col.field !== "region" && col.field !== "country"
      );
      setFilteredColumns(filteredColumnsList);
    } else {
      setFilteredColumns(columns);
    }
  };

  const fetchTiersForEdit = async (selectedRow) => {
    setSelectedRowData(selectedRow);
    try {
      setLoading(true);
      const tierList = await getCompanyTiers();
      const filteredList = tierList?.data.filter(
        (item) => item.tiercode !== "PO"
      );
      const updatedData = filteredList.map((item) => ({
        ...item,
        label: item.tiername,
        value: item.id,
      }));
      setCompanyTiers(updatedData);

      if (selectedRow.tierCode === "SEC") fetchParentOrganization();
      else if (selectedRow.tierCode === "BU") fetchSectors("editmodal");
      else if (selectedRow.tierCode === "SBU")
        fetchBusinessUnits("editmodal");
      else if (selectedRow.tierCode === "LE")
        fetchSubBusinessUnits("editmodal");

      setOrganizationData((prevData) => ({
        ...prevData,
        tierCode: selectedRow.tierCode,
        type: selectedRow.tierId,
        parentorgId: selectedRow.parentOrgId,
        parentId: selectedRow.parentOrgId,
        // parentorgId: selectedRow.parentOrgId
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCountryList = (data) => {
    const selectedRegionCountries = regionList.filter(
      (item) => item.regionId === data.regionId
    );
    let allCountries = selectedRegionCountries[0].countries?.map(
      (item) => ({
        ...item,
        value: item.countryId,
        label: item.countryName,
      })
    );

    setOrganizationData((prevData) => ({
      ...prevData,
      organizationName: data.name,
      regionName: data.regionId,
      countryName: data.countryId,
    }));

    setCountryList(allCountries);
  };

  const handleOpenModal = async (type, data = null) => {
    setModalType(type);
    if (data) {
      if (data.tierCode === "LE") {
        getCountryList(data);
      } else {
        setOrganizationData((prevData) => ({
          ...prevData,
          organizationName: data.name,
          regionName: data.regionId,
          countryName: data.countryId,
        }));
      }
      fetchTiersForEdit(data);
    }

    if (data === null) {
      setEditingEntity(null);
      if (type === "upload") {
        fetchSubBUList();
        // setFilterType('sector');
      } else {
        fetchTiers(); //Fetch company tiers
        fetchParentOrganization(); //Fetch parent organization
        // setFilterType('sector');
      }
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOrganizationData({
      organizationName: "",
      type: "",
      immediateParent: "",
      regionName: "",
      countryName: "",
      tierCode: "",
      parentorgId: "",
      parentId: "",
    });
    setEditingEntity(null);
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.files[0];
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (file) {
      if (!validTypes.includes(e?.files[0]?.type)) {
        toast.error(t("invalidFileType"), {
          position: "top-center",
        });
        return;
      } else {
        setSelectedFile(file);
        setFormData((prevData) => ({
          ...prevData,
          file: file,
        }));
      }
    } else {
      alert("No file selected.");
    }
  };

  const handleUploadFile = async () => {
    if (selectedFile) {
      try {
        // await new Promise((resolve) => setTimeout(resolve, 1000));
        // alert(`File "${selectedFile.name}" uploaded successfully!`);
        // handleCloseModal();
        setLoading(true);
        const payload = new FormData();
        let subbuId = formData.orgId;
        if (typeof formData.orgId !== "number")
          subbuId = parseInt(subbuId);

        payload.append("ParentOrgId", subbuId);
        payload.append("File", formData.file);
        await uploadOrganizationFiles(payload).then((response) => {
          if (response === null) {
            setLoading(false);
          } else {
            if (
              response?.statusCode === 200 ||
              response?.statusCode === 201
            ) {
              handleCloseModal();
              fetchSectors();
              if (response?.statusCode === 200) {
                toast.success(t("fileUploadedSuccessfully"));
              }
            }
          }
        });
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error(t("fileUploadFailedError"), { position: "top-center" });
      } finally {
        setLoading(false);
      }
    } else {
      toast.error(t("fileRequired"), {
        position: "top-center",
        theme: "dark",
      });
    }
  };

  const handleInputChange = (target, field) => {
    if (field === "file") {
      handleFileChange(target);
    } else {
      toast.dismiss();
      let selectedTypeId = target.value;
      if (target.name === "type") {
        selectedTypeId = parseInt(target.value);

        let tierName = companyTiers?.filter(
          (item) => item.id === selectedTypeId
        )[0].tiercode;
        if (tierName === "SEC") fetchParentOrganization();
        else if (tierName === "BU") fetchSectors("addModal");
        else if (tierName === "SBU") fetchBusinessUnits("addModal");
        else if (tierName === "LE") {
          fetchSubBusinessUnits("addModal");
          setOrganizationData((prev) => ({
            ...prev,
            [target.name]: target.value,
            regionName: regionList[0].regionId,
            countryName: countryList[0].countryId,
          }));
        }
      }
      setOrganizationData((prev) => ({
        ...prev,
        [target.name]:
          target.name === "organizationName"
            ? target.value.toUpperCase()
            : target.value,
        tierCode:
          target.name === "type"
            ? (companyTiers?.filter(
              (item) => item.id === selectedTypeId
            ))[0].tiercode
            : prev.tierCode,
        parentId:
          target.name === "parentorgId"
            ? (parentList?.filter(
              (item) => item.id === parseInt(target.value)
            ))[0].parentOrgId
            : prev.parentId,
      }));

      setFormData((prevData) => ({
        ...prevData,
        orgId: target.value,
      }));

      if (target.name === "regionName") {
        let selectedRegionId = target.value;
        if (typeof target.value !== "number")
          selectedRegionId = parseInt(target.value);

        const selectedRegionCountries = regionList.filter(
          (item) => item.regionId === selectedRegionId
        );

        let allCountries = selectedRegionCountries[0].countries?.map(
          (item) => ({
            ...item,
            value: item.countryId,
            label: item.countryName,
          })
        );

        setOrganizationData((prev) => ({
          ...prev,
          [target.name]: target.value,
          countryName: allCountries[0].countryId,
        }));

        setCountryList(allCountries);
      }
    }
  };

  // const validateForm = () => {
  //   const missingFields = fieldsData
  //     .filter((field) => field.required && modalType === "add" && !organizationData[field.name])
  //     .map((field) => field.fieldName);

  //   if (missingFields.length > 0) {
  //     alert(`Please fill in the required fields: ${missingFields.join(", ")}`);
  //     return false;
  //   }
  //   return true;
  // };

  const trimText = (text) => {
    return text.trim().replace(/\s+/g, " ");
  };

  const handleAddOrganization = async () => {
    // setLoading(true);
    const requiredFields = [
      {
        label: t("organizationName"),
        key: "Organization Name",
        type: "text",
        value: trimText(organizationData.organizationName),
      },
      { label: t("type"), key: "Type", type: "text", value: organizationData.type },
    ];
    const isValid = validateFields(
      organizationData,
      requiredFields,
      "byValue"
    );
    if (!isValid) {
      setLoading(false);
      return;
    }

    let payload = {
      organizationName: trimText(organizationData.organizationName),
      tierId: parseInt(organizationData.type),
      tierCode: organizationData.tierCode,
      parentId: organizationData.parentorgId,
    };

    if (organizationData.tierCode === "LE") {
      payload["regionId"] = organizationData.regionName;
      payload["countryId"] = organizationData.countryName;
    }

    await addNewOrganization(payload).then((response) => {
      if (response === null) {
        setLoading(false);
      } else {
        handleCloseModal();
        setLoading(false);
        if (filterType === "sector") fetchSectors();
        else if (filterType === "businessUnit") fetchBusinessUnits();
        else if (filterType === "subBusinessUnit")
          fetchSubBusinessUnits();
        else if (filterType === "legalEntity") fetchLegalEntities();
      }
    });
  };

  const handleEditOrganization = async () => {
    const requiredFields = [
      {
        label: t("organizationName"),
        key: "Organization Name",
        type: "text",
        value: trimText(organizationData.organizationName),
      },
      { label: t("type"), key: "Type", type: "text", value: organizationData.type },
    ];
    const isValid = validateFields(
      organizationData,
      requiredFields,
      "byValue"
    );
    if (!isValid) {
      setLoading(false);
      return;
    }

    let payload = {
      organizationName: trimText(organizationData.organizationName),
      tierId: parseInt(organizationData.type),
      parentId: organizationData.parentorgId,
      // id: selectedRowData.id
    };

    if (organizationData.tierCode === "LE") {
      payload["regionId"] = organizationData.regionName;
      payload["countryId"] = organizationData.countryName;
    }
    await updateOrganization(payload, selectedRowData.id).then(
      (response) => {
        if (response === null) {
          setLoading(false);
        } else {
          handleCloseModal();
          setLoading(false);
          if (filterType === "sector") fetchSectors();
          else if (filterType === "businessUnit")
            fetchBusinessUnits();
          else if (filterType === "subBusinessUnit")
            fetchSubBusinessUnits();
          else if (filterType === "legalEntity") fetchLegalEntities();
        }
      }
    );

    // const updated = organizations.map((organization) =>
    //   organization.organizationName === editingEntity
    //     ? { ...organization, ...organizationData }
    //     : organization
    // );
    // setOrganizations(updated);
    // handleCloseModal();
  };

  const fieldsData = useMemo(() => {
    if (modalType === "upload") {
      return [
        {
          fieldName: t("selectSubBusinessUnit"),
          name: "subbu",
          type: "dropdown",
          options: subBUList,
          required: true,
        },
        {
          name: "file",
          type: "file",
          showUploadIcon: true,
        },
      ];
    }

    const baseFields = [
      {
        fieldName: t("organizationName"),
        name: "organizationName",
        type: "input",
        required: true,
      },
      {
        fieldName: t("type"),
        name: "type",
        type: "dropdown",
        options: companyTiers,
        isFieldDisabled: modalType === "edit" ? true : false,
        // [
        //   { label: "Sector", value: "Sector" },
        //   { label: "Business Unit", value: "Business Unit" },
        //   { label: "Sub Business Unit", value: "Sub Business Unit" },
        //   { label: "Legal Entity", value: "Legal Entity" },
        // ],
        required: true,
      },
      {
        fieldName:  t("immediateParent"),
        name: "parentorgId",
        type: "dropdown",
        required: true,
        options: parentList,
      },
    ];

    if (organizationData.tierCode === "LE") {
      baseFields.push(
        {
          fieldName: t("region"),
          name: "regionName",
          type: "dropdown",
          required: true,
          options: regionList,
        },
        {
          fieldName:  t("country"),
          name: "countryName",
          type: "dropdown",
          required: true,
          options: countryList,
        }
      );
    }

    return baseFields;
  }, [
    modalType,
    organizationData.type,
    companyTiers,
    parentList,
    subBUList,
    countryList,
    i18n.language
  ]);

  const handleChange = (event) => {
    setFilterType(event.target.value);
    if (event.target.value === "legalEntity") {
      setSelectedFilter(event.target.value);
    }
  };

  const handleDownloadSampleTemplate = async () => {
    try {
      setLoading(true);
      const result = await downloadOrgFile();
      const blob = new Blob([result], {
        // MIME type for Excel files
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SampleOrganizationTemplate.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="org-manag-Container">
          {/* Top Action Buttons */}
          <div
            className="org-mang-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {" "}
            <div className="surveyHeader">
              <h2 className="surveyTitle">{t("listOfOrganizations")}</h2>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                className="org-download-btn"
                onClick={handleDownloadSampleTemplate}
              >
                {t("downloadOrganizationTemplate")}
                <img
                  src={DownloadIcon}
                  alt="Download"
                  style={{ width: "16px", height: "18px" }}
                />
              </button>
              <IconButton
                text={t("uploadOrganizationFile")}
                onClick={() => handleOpenModal("upload")}
                style={{
                  backgroundColor: "#F0F0F0",
                  color: "#454647",
                  border: "1px solid #B3B2B4",
                  marginLeft: "10px",
                  padding: "7px 10px",
                }}
              />
              <IconButton
                text={t("addOrganization")}
                onClick={() => handleOpenModal("add")}
                style={{
                  backgroundColor: "#F0F0F0",
                  color: "#454647",
                  border: "1px solid #B3B2B4",
                  marginLeft: "10px",
                }}
                icon={<PlusIcon />}
              />
            </div>
          </div>

          <hr></hr>

          {/* Filter Section */}
          <div
            style={{
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              marginTop: "15px",
              gap: "20px",
            }}
          >
            <label>
              <input
                type="radio"
                name="filterType"
                value="sector"
                checked={filterType === "sector"}
                onChange={handleChange}
              />{" "}
              {t("sector")}
            </label>
            <label>
              <input
                type="radio"
                name="filterType"
                value="businessUnit"
                checked={filterType === "businessUnit"}
                onChange={handleChange}
              />{" "}
              {t("businessUnit")}
            </label>
            <label>
              <input
                type="radio"
                name="filterType"
                value="subBusinessUnit"
                checked={filterType === "subBusinessUnit"}
                onChange={handleChange}
              />{" "}
              {t("subBusinessUnit")}
            </label>
            <label>
              <input
                type="radio"
                name="filterType"
                value="legalEntity"
                checked={filterType === "legalEntity"}
                onChange={handleChange}
              />{" "}
              {t("entity")}
            </label>
            <button
              style={{
                marginLeft: "auto",
                border: "1px solid red",
                color: "black",
                background: "white",
                padding: "5px 15px",
              }}
              onClick={onSearchData}
            >
              {t("search")}
            </button>
          </div>

          {/* Table Section */}
          <div style={{ marginTop: "1rem" }}>
            <DataTableComponent
              columns={filteredColumns}
              data={organizations}
              tableName="OrganizationManagement"
              onEditDetails={(row) =>
                handleOpenModal("edit", row)
              }
              pageCount={
                filterType && filterType === "legalEntity"
                  ? 50
                  : 5
              }
              selectedFilter={selectedFilter}
            />
          </div>

          {/* Modal */}
          <ModalComponent
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            modalTitle={
              modalType === "upload"
                ? t("organizationFileUpload")
                : modalType === "add"
                  ? t("addOrganization")
                  : t("updateOrganization")
            }
            fieldsData={fieldsData}
            formData={organizationData}
            onChangeInput={handleInputChange}
            // {
            //   modalType === "upload" ? handleFileChange : handleInputChange
            // }
            buttons={[
              isUploadMode && (
                <button
                  key="upload"
                  className="saveButton"
                  onClick={handleUploadFile}
                >
                  {t("upload")}
                </button>
              ),
              (isAddMode || isEditMode) && (
                <button
                  key="addOrEdit"
                  className="saveButton"
                  onClick={
                    isEditMode
                      ? handleEditOrganization
                      : handleAddOrganization
                  }
                >
                  {isEditMode ? t("update") : t("add")}
                </button>
              ),
              <button
                key="cancel"
                className="cancelButton"
                onClick={handleCloseModal}
              >
                {t("cancel")}
              </button>,
            ].filter(Boolean)}
          />
        </div>
      )}
    </>
  );
};

export default OrganizationManagement;